/**
 * What player-web's Home and Stats say about the caller's own results (known gap 78, phase 2), from
 * `GET /api/rooms/{roomId}/players/me/results`. Pure and importing nothing, so `ownResults.test.js` runs it
 * under `node --test` as `membership.test.js` runs its module.
 */

/** The totals the read reports. A structural copy of `OwnResultTotals`, since this module imports nothing. */
export interface Totals {
  entries: number;
  cashes: number;
  wins: number;
  winnings: number;
  buyInsSpent: number;
  entriesWithBuyins: number;
  bestFinish: number | null;
  finalTables: number;
}

/** How many of the newest results Home lists. */
export const HOME_RECENT = 5;

/** A page of Stats' history: the server's default page, inside its cap of 200. */
export const STATS_PAGE_SIZE = 50;

/**
 * What Home and Stats say on somebody else's portal — staff browsing it read-only — where the caller's own
 * results are not theirs to show (gap 78's decision: the read is the reader's own).
 */
export const NOT_YOURS =
  "A player's results are shown to the player holding the card. Staff see a player's figures on room-web's Players screen.";

export type ReadState<T> =
  | { kind: 'notYours' }
  | { kind: 'loading' }
  | { kind: 'failed'; message: string }
  | { kind: 'ready'; data: T };

/**
 * Which of four things a screen is showing — and never "no results" for a read that has not answered.
 *
 * Known gap 93: Home and Stats took `data ?? []`, so a read still on its way (52 s for SHRPO Demo's capped
 * list on 2026-10-13) said "0 recorded finishes" and "No results recorded yet", and a failed one said the
 * same for good. Somebody else's portal — staff browsing it read-only — is not read at all: the read is the
 * caller's own card, so the API would refuse it, and that refusal is neither a failure to show nor a cue to
 * recheck the caller's memberships.
 */
export function readState<T>(q: {
  readOnly: boolean;
  isPending: boolean;
  failure: string | null;
  data: T | undefined;
}): ReadState<T> {
  if (q.readOnly) return { kind: 'notYours' };
  if (q.data !== undefined) return { kind: 'ready', data: q.data };
  if (q.failure !== null) return { kind: 'failed', message: q.failure };
  return { kind: 'loading' };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The line under Home's *Entries* card. The card counts every result — each tournament entered, placed or
 * not — and this names the cashes beside it, so it cannot read as a second cash count disagreeing with the
 * header's (known gap 95: it said "Recorded finishes: 7" under "1 cash").
 */
export function entriesHint(totals: Totals): string {
  return totals.cashes === 0 ? 'No cashes yet' : plural(totals.cashes, 'cash', 'cashes');
}

/**
 * The line under Buy-ins saying how far to trust it (known gap 95). Buy-ins count re-entries — each result's
 * buy-in times its bullets — and the room records bullets on some results and not others; a result without
 * them counts once, the player having entered at least once. So the figure is exact only where every result
 * recorded them, and the line says which case this is.
 */
export function buyInsCoverage(totals: Totals): string | null {
  if (totals.entries === 0) return null;
  if (totals.entriesWithBuyins === totals.entries) return 'Re-entries recorded on every result.';
  if (totals.entriesWithBuyins === 0) return 'No result recorded its re-entries, so each counts as one entry.';
  return `Re-entries recorded on ${totals.entriesWithBuyins} of ${totals.entries} results; the rest count as one entry each.`;
}

/** Winnings less buy-ins, re-entries included (known gap 95). */
export function net(totals: Totals): number {
  return totals.winnings - totals.buyInsSpent;
}

/** What Stats' figures are over: every result, which is every entry, and the final tables among them. */
export function basisLine(totals: Totals): string {
  return `Based on ${plural(totals.entries, 'entry', 'entries')} and ${plural(totals.finalTables, 'final table', 'final tables')}.`;
}

/** A result's place, or a dash for an entry that did not reach one — never blank, never 0. */
/**
 * A card's figure from a read: its number once there is one, an ellipsis while it is on its way and a dash
 * when it failed or is not this caller's to read. Never 0 for a read that has not answered (known gap 93) —
 * Home's Leaderboards and Fantasy cards had the same `data ?? []` as its results.
 */
export function figureText<T>(state: ReadState<T>, pick: (data: T) => number): string {
  if (state.kind === 'ready') return String(pick(state.data));
  return state.kind === 'loading' ? '…' : '—';
}

export function placeLabel(position: number | null): string {
  return position === null ? '—' : String(position);
}

/** Dollars as the screens print them, the same in every locale so a test can pin them. */
export const dollars = (n: number) => `$${n.toLocaleString('en-US')}`;

/** A result's buy-in, naming the bullets when there was more than one — what Buy-ins sums. */
export function buyInLine(buyIn: number, buyins: number | null): string {
  return buyins !== null && buyins > 1 ? `${buyins} × ${dollars(buyIn)} buy-in` : `${dollars(buyIn)} buy-in`;
}

/**
 * How many of the room's leaderboards are running — Home's *Running at* card (known gap 94). The list it is
 * given carries drafts and closed boards too, which are neither running nor anything a player can follow yet;
 * a status this build does not know is not counted, rather than guessed at.
 */
export function runningCount(boards: readonly { status: string }[]): number {
  return boards.filter((b) => b.status === 'running').length;
}

/** Pages in a history of <total> results; an empty history is one empty page, so a pager never reads "0 of 0". */
export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * Home's header counts, in words that follow their number (known gap 98): "1 win", "1 cash", "0 wins". The
 * figures are the player read's (`GET /api/players/{id}`); only the noun was wrong — it read "1 cashes".
 */
export function headerCounts(player: { wins: number; cashes: number }): { wins: string; cashes: string } {
  return { wins: plural(player.wins, 'win', 'wins'), cashes: plural(player.cashes, 'cash', 'cashes') };
}
